import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Empty, Input, Pagination, Select, Tooltip, message } from 'antd'
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MobileOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { getUploadConfig, setPipelineApiScope, triggerBrowserDownload, type UploadConfig } from '../../../api/client'
import UploadModal from '../../../components/UploadModal'
import { workspaceFlatImagesBaseUrl } from '../../api'
import MobileCaptureQrModal from '../../components/MobileCaptureQrModal'
import { VistoeraActionConfirm } from '../../ui/action'
import {
  batchStartFlatChains,
  deleteFlatChain,
  listFlatChains,
  listFlatTemplates,
  retryFlatChain,
  type FlatChainListSummary,
  type FlatChainOut,
  type FlatChainStatus,
  type FlatTemplateOut,
} from '../../flatImageApi'
import { useWorkspaceState } from '../WorkspaceContext'
import './flat-image.css'

const FLAT_STATUS_KEYS: Array<FlatChainStatus | 'all'> = [
  'all',
  'pending',
  'processing',
  'waiting_next',
  'needs_review',
  'done',
  'failed',
]

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
function formatDims(w: number | null, h: number | null): string {
  return w && h ? `${w} × ${h}` : '—'
}
function formatTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

const POLL_ACTIVE_MS = 2000
const POLL_IDLE_MS = 10000

export default function FlatImageListPage() {
  const { t } = useTranslation(['imageProcessing', 'autoProcess', 'actions', 'navigation'])
  const { workspace } = useWorkspaceState()
  const workspaceId = workspace?.id || ''
  const navigate = useNavigate()

  const imageAssetsLabel = t('navigation:tenantNav.images.assets')
  const flatOptimizationLabel = t('navigation:tenantNav.images.flatOptimization')

  const templateLabel = (key: string) => {
    const known = t(`templates.${key}`, { defaultValue: '' })
    return known || key
  }

  const [scopeReady, setScopeReady] = useState(false)
  useEffect(() => {
    if (!workspaceId) return
    setPipelineApiScope(workspaceFlatImagesBaseUrl(workspaceId))
    setScopeReady(true)
    return () => {
      setPipelineApiScope(null)
      setScopeReady(false)
    }
  }, [workspaceId])

  const [templates, setTemplates] = useState<FlatTemplateOut[]>([])
  const [templateFilter, setTemplateFilter] = useState<string>('all')
  const [status, setStatus] = useState<FlatChainStatus | 'all'>('all')
  const [q, setQ] = useState('')
  const [chains, setChains] = useState<FlatChainOut[]>([])
  const [summary, setSummary] = useState<FlatChainListSummary | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(24)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string[]>([])

  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadConfig, setUploadConfig] = useState<UploadConfig | null>(null)
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileCapturePolling, setMobileCapturePolling] = useState(false)

  const requestSeqRef = useRef(0)
  const load = useCallback(
    async (pageOverride?: number) => {
      if (!workspaceId || !scopeReady) return null
      const seq = ++requestSeqRef.current
      setLoading(true)
      try {
        const { data } = await listFlatChains(workspaceId, {
          q: q || undefined,
          status: status === 'all' ? undefined : status,
          template_key: templateFilter === 'all' ? undefined : templateFilter,
          page: pageOverride ?? page,
          page_size: pageSize,
        })
        if (seq !== requestSeqRef.current) return data.summary
        setChains(data.chains)
        setSummary(data.summary)
        setPage(data.page)
        setTotalPages(data.total_pages)
        return data.summary
      } finally {
        if (seq === requestSeqRef.current) setLoading(false)
      }
    },
    [workspaceId, scopeReady, q, status, templateFilter, page, pageSize],
  )

  useEffect(() => {
    if (!workspaceId || !scopeReady) return
    load().catch(() => undefined)
  }, [workspaceId, scopeReady, load])

  useEffect(() => {
    if (!scopeReady) return
    listFlatTemplates(workspaceId)
      .then(({ data }) => setTemplates(data.templates))
      .catch(() => undefined)
    getUploadConfig()
      .then(({ data }) => setUploadConfig(data))
      .catch(() => undefined)
  }, [scopeReady, workspaceId])

  const pollInFlightRef = useRef(false)
  useEffect(() => {
    if (!workspaceId || !scopeReady) return
    let cancelled = false
    let timer: number | undefined
    const tick = async () => {
      let nextDelay = POLL_ACTIVE_MS
      if (!pollInFlightRef.current) {
        pollInFlightRef.current = true
        try {
          const s = await load()
          if (s) {
            const outstanding = s.total - s.done - s.failed - s.needs_review
            nextDelay = outstanding > 0 || mobileCapturePolling ? POLL_ACTIVE_MS : POLL_IDLE_MS
          } else if (mobileCapturePolling) {
            nextDelay = POLL_ACTIVE_MS
          }
        } catch {
          // keep polling at the active rate on a transient hiccup
        } finally {
          pollInFlightRef.current = false
        }
      }
      if (!cancelled) timer = window.setTimeout(tick, nextDelay)
    }
    timer = window.setTimeout(tick, POLL_ACTIVE_MS)
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [workspaceId, scopeReady, load, mobileCapturePolling])

  const toggleSelect = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  const toggleSelectAll = () =>
    setSelected((prev) => (prev.length === chains.length ? [] : chains.map((c) => c.id)))

  const onBatchStart = async (templateKey?: string) => {
    if (!selected.length) return
    try {
      const { data } = await batchStartFlatChains(workspaceId, selected, templateKey)
      message.success(t('toast.batchQueued', { count: data.count }))
      setSelected([])
      await load()
    } catch (e) {
      message.error((e as Error).message || t('toast.batchFailed'))
    }
  }

  const onRetry = async (chain: FlatChainOut) => {
    try {
      await retryFlatChain(workspaceId, chain.id)
      message.success(t('toast.retryQueued'))
      await load()
    } catch (e) {
      message.error((e as Error).message || t('toast.retryFailed'))
    }
  }

  const deleteChain = async (chain: FlatChainOut) => {
    try {
      await deleteFlatChain(workspaceId, chain.id)
      message.success(t('toast.deleted'))
      await load()
    } catch (e) {
      message.error((e as Error).message || t('toast.deleteFailed'))
      throw e
    }
  }

  const canonicalTemplates = templates.filter((tpl) => !tpl.is_custom)
  const customTemplates = templates.filter((tpl) => tpl.is_custom)

  if (!workspaceId) return null

  return (
    <div className="fi-page">
      <div className="fi-crumb">
        <Link to={`/workspace/${workspaceId}/images`}>{imageAssetsLabel}</Link>
        <span className="sep">›</span>
        <span className="current">{flatOptimizationLabel}</span>
        <span className="sep">·</span>
        <Link to={`/workspace/${workspaceId}/images`}>
          <ArrowLeftOutlined /> {t('list.backToAssets')}
        </Link>
      </div>

      <div className="fi-header">
        <div>
          <h1 className="fi-title">{flatOptimizationLabel}</h1>
          <p className="fi-subtitle">{t('list.subtitle')}</p>
        </div>
        <div className="fi-actions">
          <Button icon={<CloudUploadOutlined />} type="primary" onClick={() => setUploadOpen(true)} data-testid="fi-open-upload-btn">
            {t('list.browserUpload')}
          </Button>
          <Button icon={<MobileOutlined />} onClick={() => setMobileCaptureOpen(true)} data-testid="fi-open-mobile-capture-btn">
            {t('list.mobileUpload')}
          </Button>
          <Tooltip title={selected.length ? '' : t('list.selectFirst')}>
            <Button icon={<PlayCircleOutlined />} disabled={!selected.length} onClick={() => onBatchStart()}>
              {selected.length ? t('list.batchStartWithCount', { count: selected.length }) : t('list.batchStart')}
            </Button>
          </Tooltip>
          <Tooltip title={t('actions:refresh')}>
            <Button icon={<ReloadOutlined />} onClick={() => load()} />
          </Tooltip>
        </div>
      </div>

      <div className="fi-template-bar">
        <span className="fi-template-label">{t('list.templateLabel')}</span>
        <button
          type="button"
          className={`fi-template-chip${templateFilter === 'all' ? ' is-active' : ''}`}
          onClick={() => setTemplateFilter('all')}
        >
          {t('list.allTemplates')}
        </button>
        {canonicalTemplates.map((tpl) => (
          <button
            key={tpl.key}
            type="button"
            className={`fi-template-chip${templateFilter === tpl.key ? ' is-active' : ''}`}
            onClick={() => setTemplateFilter(tpl.key)}
          >
            {tpl.label}
          </button>
        ))}
        {customTemplates.length > 0 && (
          <button
            type="button"
            className={`fi-template-chip${templateFilter === 'custom' ? ' is-active' : ''}`}
            onClick={() => setTemplateFilter('custom')}
          >
            {t('list.customTemplates', { count: customTemplates.length })}
          </button>
        )}
      </div>

      <div className="fi-toolbar">
        <div className="fi-status-tabs">
          {FLAT_STATUS_KEYS.map((tabKey) => {
            const count =
              tabKey === 'all' ? summary?.total ?? 0 : (summary?.[tabKey as keyof FlatChainListSummary] as number) ?? 0
            return (
              <button
                key={tabKey}
                type="button"
                className={`fi-status-tab${status === tabKey ? ' is-active' : ''}`}
                onClick={() => {
                  setStatus(tabKey)
                  setPage(1)
                }}
              >
                {t(`statusFilter.${tabKey}`)}
                <span className="count">{count}</span>
              </button>
            )
          })}
        </div>
        <Input
          placeholder={t('list.searchPlaceholder')}
          prefix={<SearchOutlined />}
          style={{ width: 220 }}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setPage(1)
          }}
          onPressEnter={() => load(1)}
          allowClear
        />
      </div>

      {chains.length === 0 && !loading ? (
        <Empty description={t('list.empty')} style={{ marginTop: 60 }} />
      ) : (
        <div className="fi-table-wrap">
          <table className="fi-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <Checkbox
                    checked={chains.length > 0 && selected.length === chains.length}
                    indeterminate={selected.length > 0 && selected.length < chains.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>{t('list.table.original')}</th>
                <th>{t('list.table.rectifiedPreview')}</th>
                <th>{t('list.table.enhancedOutput')}</th>
                <th>{t('list.table.template')}</th>
                <th>{t('list.table.status')}</th>
                <th>{t('list.table.dimensions')}</th>
                <th>{t('list.table.size')}</th>
                <th>{t('list.table.uploadedAt')}</th>
                <th>{t('list.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {chains.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Checkbox checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} />
                  </td>
                  <td>
                    <div className="fi-table-thumb">
                      {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" /> : <span>{t('list.noPreview')}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="fi-table-thumb">
                      {c.flat_rectified_url ? <img src={c.flat_rectified_url} alt="" /> : <span>{t('list.notGenerated')}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="fi-table-thumb">
                      {c.flat_enhanced_url ? <img src={c.flat_enhanced_url} alt="" /> : <span>{t('list.notGenerated')}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="fi-row-name">{templateLabel(c.flat_template_key ?? '') || t('list.notSelected')}</div>
                  </td>
                  <td>
                    <span className={`fi-status-badge ${c.status}`}>{c.status_label}</span>
                    {c.current_message && c.status !== 'done' && <div className="fi-row-sub">{c.current_message}</div>}
                  </td>
                  <td>{formatDims(c.original_width, c.original_height)}</td>
                  <td>{formatBytes(c.original_size_bytes)}</td>
                  <td>{formatTime(c.created_at)}</td>
                  <td>
                    <Tooltip title={t('list.edit')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/workspace/${workspaceId}/images/flat-optimization/${c.id}`)}
                      />
                    </Tooltip>
                    {c.status === 'failed' && (
                      <Tooltip title={t('list.retry')}>
                        <Button type="text" size="small" icon={<PlayCircleOutlined />} onClick={() => onRetry(c)} />
                      </Tooltip>
                    )}
                    {c.flat_enhanced_url && (
                      <Tooltip title={t('list.downloadEnhanced')}>
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => triggerBrowserDownload(c.flat_enhanced_url!, c.original_filename)}
                        />
                      </Tooltip>
                    )}
                    <VistoeraActionConfirm
                      title={t('list.deleteTitle')}
                      description={t('list.deleteDescription', { filename: c.original_filename })}
                      confirmLabel={t('actions:delete')}
                      tooltip={t('actions:delete')}
                      onConfirm={() => deleteChain(c)}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label={t('actions:delete')} />
                    </VistoeraActionConfirm>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {chains.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('list.paginationSummary', { total: summary?.total ?? 0, page, totalPages })}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Select
              size="small"
              value={pageSize}
              style={{ width: 100 }}
              onChange={(v) => {
                setPageSize(v)
                load(1)
              }}
              options={[24, 48, 100].map((n) => ({ value: n, label: t('list.pageSize', { size: n }) }))}
            />
            <Pagination simple current={page} pageSize={pageSize} total={summary?.total ?? 0} onChange={(p) => load(p)} />
          </div>
        </div>
      )}

      {scopeReady && (
        <UploadModal
          open={uploadOpen}
          config={uploadConfig}
          onClose={() => setUploadOpen(false)}
          onAnyCompleted={() => load(1)}
          autoProcess={false}
          assetSource="browser_upload"
          title={t('list.uploadModalTitle')}
        />
      )}

      <MobileCaptureQrModal
        open={mobileCaptureOpen}
        workspaceId={workspaceId}
        targetFlow="flat_image_processing"
        onClose={() => setMobileCaptureOpen(false)}
        onSessionActiveChange={setMobileCapturePolling}
      />
    </div>
  )
}
